import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import { BLOG_POSTS } from "@/data/content";
import { BLOG_PAGE } from "@/constants/testIds";

export default function Blog() {
  const { t } = useTranslation();
  const [featured, ...rest] = BLOG_POSTS;

  return (
    <div className="relative">
      {/* HEADER */}
      <section className="relative pt-24 md:pt-36 pb-20">
        <div className="container-x">
          <Reveal><p className="eyebrow mb-6">{t("blog.eyebrow")}</p></Reveal>
          <Reveal delay={80}>
            <h1 className="display-h1 max-w-5xl">
              {t("blog.heroTitle1")} <span className="italic text-clay-400">{t("blog.heroTitleItalic")}</span>{t("blog.heroTitle2")}
            </h1>
          </Reveal>
          <Reveal delay={160}>
            <p className="body-lg mt-10 max-w-2xl">{t("blog.heroDescription")}</p>
          </Reveal>
        </div>
      </section>

      {/* FEATURED */}
      <section className="pb-16">
        <div className="container-x">
          <Reveal>
            <Link
              to={`/blog/${featured.slug}`}
              data-testid={`${BLOG_PAGE.cardPrefix}-${featured.slug}`}
              className="border-t border-b border-white/10 py-12 md:py-16 grid grid-cols-1 md:grid-cols-12 gap-10 group cursor-pointer block"
            >
              <div className="md:col-span-3 space-y-3">
                <div className="text-xs uppercase tracking-widest2 text-clay-400">
                  {t("blog.featured")} · {featured.category}
                </div>
                <div className="text-xs uppercase tracking-widest2 text-bone-200/50">
                  {featured.date} · {featured.readTime}
                </div>
              </div>
              <div className="md:col-span-9">
                <h2 className="display-h2 mb-6 transition-colors group-hover:text-clay-400">
                  {featured.title}
                </h2>
                <p className="body-lg max-w-3xl mb-8">{featured.excerpt}</p>
                <span className="link-arrow text-sm font-semibold uppercase tracking-widest2">
                  {t("common.readEssay")}
                  <ArrowRight size={14} />
                </span>
              </div>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* LIST */}
      <section className="pb-24 md:pb-32">
        <div className="container-x">
          <ul className="border-t border-white/10">
            {rest.map((p, i) => (
              <Reveal key={p.slug} delay={i * 60}>
                <li className="border-b border-white/10">
                  <Link
                    to={`/blog/${p.slug}`}
                    data-testid={`${BLOG_PAGE.cardPrefix}-${p.slug}`}
                    className="block py-10 md:py-14 group cursor-pointer grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 transition-colors hover:bg-white/[0.015]"
                  >
                    <div className="md:col-span-3 space-y-2">
                      <div className="text-xs uppercase tracking-widest2 text-clay-400">
                        {p.category}
                      </div>
                      <div className="text-xs uppercase tracking-widest2 text-bone-200/50">
                        {p.date} · {p.readTime}
                      </div>
                    </div>
                    <div className="md:col-span-8">
                      <h3 className="display-h3 mb-4 transition-colors group-hover:text-clay-400">
                        {p.title}
                      </h3>
                      <p className="text-bone-200/70 leading-relaxed max-w-2xl">
                        {p.excerpt}
                      </p>
                    </div>
                    <div className="md:col-span-1 md:flex md:items-start md:justify-end">
                      <ArrowUpRight
                        size={22}
                        className="text-bone-100/40 transition-all duration-500 group-hover:text-clay-400 group-hover:-translate-y-1 group-hover:translate-x-1"
                      />
                    </div>
                  </Link>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      {/* NEWSLETTER CTA */}
      <section className="py-24 md:py-32 bg-ink-900/40 border-y border-white/5">
        <div className="container-x text-center max-w-3xl mx-auto">
          <Reveal>
            <p className="eyebrow justify-center mb-6">{t("blog.newsletterEyebrow")}</p>
            <h2 className="display-h2 mb-6">{t("blog.newsletterTitle")}</h2>
            <p className="body-lg mb-10 max-w-xl mx-auto">{t("blog.newsletterBody")}</p>
            <Link to="/contact" className="btn-primary">
              {t("blog.newsletterCta")}
              <ArrowUpRight size={16} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
